import axios from 'axios';
import { API_URL } from '../context/connection-string';
import { toast } from 'react-toastify';
import errorLogger from '../utils/errorLogger';
import apiErrorTracker from '../utils/apiErrorTracker';

/**
 * Centralized API Client for Payroll Application
 * 
 * Features:
 * - Automatic timeout handling (30 seconds)
 * - Request/response interceptors
 * - Token injection from Redux store
 * - Error handling and standardization
 * - Request cancellation support
 * - Retry logic for failed requests
 */

// Create axios instance with default configuration
const apiClient = axios.create({
    baseURL: API_URL,
    timeout: 30000, // 30 seconds
    headers: {
        'Content-Type': 'application/json',
    },
});

// Store reference to Redux store for token access
let storeRef = null;

/**
 * Initialize the API client with Redux store reference
 * This should be called once in App.js or index.js
 * @param {Object} store - Redux store instance
 */
export const initializeApiClient = (store) => {
    storeRef = store;
};

/**
 * Get token from Redux store
 * @returns {string|null} - Token string or null if not available
 */
const getToken = () => {
    if (!storeRef) {
        return null;
    }
    
    try {
        const state = storeRef.getState();
        const loginData = state?.LoginDetails;
        
        if (loginData && loginData.length > 0 && loginData[0]?.token) {
            // Token is stored as { headers: { "smart-stay-token": token } }
            const tokenObj = loginData[0].token;
            if (tokenObj?.headers?.['smart-stay-token']) {
                return tokenObj.headers['smart-stay-token'];
            }
            // Fallback: if token is directly a string
            if (typeof tokenObj === 'string') {
                return tokenObj;
            }
        }
        return null;
    } catch (error) {
        console.error('Error getting token from store:', error);
        return null;
    }
};

/**
 * Request Interceptor
 * Automatically adds authentication token to all requests
 */
apiClient.interceptors.request.use(
    (config) => {
        // Get token from Redux store
        const token = getToken();
        
        if (token) {
            // Add token to headers
            config.headers['smart-stay-token'] = token;
        }
        
        // Log request in development mode
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
        }
        
        return config;
    },
    (error) => {
        // Handle request error
        console.error('[API Request Error]', error);
        return Promise.reject(error);
    }
);

/**
 * Response Interceptor
 * Handles responses, errors, and token refresh
 */
apiClient.interceptors.response.use(
    (response) => {
        // Log response in development mode
        if (process.env.NODE_ENV === 'development') {
            console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
        }
        
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        const url = originalRequest?.url || 'unknown';
        const method = originalRequest?.method?.toUpperCase() || 'UNKNOWN';
        
        // Handle network errors
        if (!error.response) {
            // Network error or timeout
            const errorMessage = error.code === 'ECONNABORTED' 
                ? 'Request timeout. Please try again.' 
                : 'Network error. Please check your internet connection.';
            
            // Log and track error
            errorLogger.apiError(url, method, 0, error, {
                code: error.code || 'NETWORK_ERROR',
                message: errorMessage
            });
            apiErrorTracker.trackError(url, method, 0, error);
            
            toast.error(errorMessage);
            return Promise.reject({
                message: errorMessage,
                code: error.code || 'NETWORK_ERROR',
                originalError: error
            });
        }
        
        const { status, data } = error.response;
        
        // Log and track all API errors
        errorLogger.apiError(url, method, status, error, {
            responseData: data
        });
        apiErrorTracker.trackError(url, method, status, error);
        
        // Handle 401 Unauthorized - Token expired or invalid
        if (status === 401) {
            // Clear login data and redirect to login
            if (storeRef) {
                try {
                    const state = storeRef.getState();
                    // Dispatch logout action if available
                    // For now, just show error
                    toast.error('Session expired. Please login again.');
                    
                    // Redirect to login after a delay
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 2000);
                } catch (e) {
                    errorLogger.error('Error handling 401', e);
                }
            }
            
            return Promise.reject({
                message: 'Unauthorized. Please login again.',
                status: 401,
                originalError: error
            });
        }
        
        // Handle 403 Forbidden
        if (status === 403) {
            toast.error('Access forbidden. You do not have permission to perform this action.');
            return Promise.reject({
                message: 'Access forbidden',
                status: 403,
                originalError: error
            });
        }
        
        // Handle 500 Server Error
        if (status >= 500) {
            toast.error('Server error. Please try again later.');
            return Promise.reject({
                message: 'Server error',
                status: status,
                originalError: error
            });
        }
        
        // Handle other errors
        const errorMessage = data?.message || data || error.message || 'An error occurred';
        
        // Don't show toast for validation errors (400) - let components handle them
        if (status !== 400) {
            toast.error(errorMessage);
        }
        
        return Promise.reject({
            message: errorMessage,
            status: status,
            data: data,
            originalError: error
        });
    }
);

/**
 * Create a request with retry logic
 * @param {Function} requestFn - Function that returns a promise
 * @param {number} maxRetries - Maximum number of retries (default: 2)
 * @param {number} retryDelay - Delay between retries in ms (default: 1000)
 * @returns {Promise} - Request promise with retry logic
 */
const withRetry = async (requestFn, maxRetries = 2, retryDelay = 1000) => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error;
            
            // Don't retry on 4xx errors (client errors)
            if (error.status && error.status >= 400 && error.status < 500) {
                throw error;
            }
            
            // Don't retry on last attempt
            if (attempt < maxRetries) {
                // Wait before retrying
                await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
                continue;
            }
        }
    }
    
    throw lastError;
};

/**
 * Enhanced GET request with retry support and AbortController
 * @param {string} url - API endpoint
 * @param {Object} config - Request configuration (supports signal for AbortController)
 * @returns {Promise} - Request promise
 */
export const get = async (url, config = {}) => {
    // If signal is provided, use it; otherwise create a new one
    const requestConfig = {
        ...config,
        signal: config.signal
    };
    
    const requestFn = () => apiClient.get(url, requestConfig);
    
    if (config.retry !== false) {
        return withRetry(requestFn, config.maxRetries, config.retryDelay);
    }
    
    return requestFn();
};

/**
 * Enhanced POST request with retry support and AbortController
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Request configuration (supports signal for AbortController)
 * @returns {Promise} - Request promise
 */
export const post = async (url, data = {}, config = {}) => {
    const requestConfig = {
        ...config,
        signal: config.signal
    };
    
    const requestFn = () => apiClient.post(url, data, requestConfig);
    
    if (config.retry !== false) {
        return withRetry(requestFn, config.maxRetries, config.retryDelay);
    }
    
    return requestFn();
};

/**
 * Enhanced PATCH request with retry support and AbortController
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Request configuration (supports signal for AbortController)
 * @returns {Promise} - Request promise
 */
export const patch = async (url, data = {}, config = {}) => {
    const requestConfig = {
        ...config,
        signal: config.signal
    };
    
    const requestFn = () => apiClient.patch(url, data, requestConfig);
    
    if (config.retry !== false) {
        return withRetry(requestFn, config.maxRetries, config.retryDelay);
    }
    
    return requestFn();
};

/**
 * Enhanced PUT request with retry support and AbortController
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Request configuration (supports signal for AbortController)
 * @returns {Promise} - Request promise
 */
export const put = async (url, data = {}, config = {}) => {
    const requestConfig = {
        ...config,
        signal: config.signal
    };
    
    const requestFn = () => apiClient.put(url, data, requestConfig);
    
    if (config.retry !== false) {
        return withRetry(requestFn, config.maxRetries, config.retryDelay);
    }
    
    return requestFn();
};

/**
 * Enhanced DELETE request with retry support and AbortController
 * @param {string} url - API endpoint
 * @param {Object} config - Request configuration (supports signal for AbortController)
 * @returns {Promise} - Request promise
 */
export const del = async (url, config = {}) => {
    const requestConfig = {
        ...config,
        signal: config.signal
    };
    
    const requestFn = () => apiClient.delete(url, requestConfig);
    
    if (config.retry !== false) {
        return withRetry(requestFn, config.maxRetries, config.retryDelay);
    }
    
    return requestFn();
};

/**
 * Create a cancellable request using AbortController
 * @param {Function} requestFn - Function that accepts signal and returns a promise
 * @returns {Object} - Object with request promise and abort function
 */
export const createCancellableRequest = (requestFn) => {
    const controller = new AbortController();
    
    const request = requestFn(controller.signal);
    
    return {
        request,
        abort: () => controller.abort(),
        signal: controller.signal
    };
};

// Export the axios instance for advanced usage
export default apiClient;

// Export convenience methods
export const api = {
    get,
    post,
    patch,
    put,
    delete: del,
    createCancellableRequest,
    client: apiClient
};

